import { Controller } from '@hotwired/stimulus'
import {
  emiteCampoEnfocado,
  emiteEnvioDelFormulario,
  emiteErrorDeCampo,
  emiteMetodoElegido,
  emiteResultadoDelNombre,
  limpiaErrorDeCampo,
  marcaAvisoDeDominio,
  marcaDominioComprobado,
  CAMPO_EMAIL,
  CAMPO_NOMBRE,
  CAMPO_PASSWORD,
  CAMPO_TERMINOS,
  ERROR_DOMINIO_DESCONOCIDO,
  ERROR_FORMATO,
  ERROR_NOMBRE_EN_USO,
  ERROR_NOMBRE_MAL_FORMADO,
  ERROR_PASSWORD_DEBIL,
  ERROR_TERMINOS,
  METODO_EMAIL,
  RESULTADO_DISPONIBLE,
  RESULTADO_EN_USO,
  RESULTADO_MAL_FORMADO
} from 'lib/analitica_del_registro'

// Feedback en tiempo real del formulario de alta (`/registro` y el paso de confirmación del alta
// con Google, que renderizan el mismo campo). TC-134-1.1.4.4.
//
// Vive aquí y no en `packs/registro.js` porque el pack es un entry point: al importarlo arranca la
// aplicación de Stimulus de la página entera y no se puede montar en un test. El pack se queda con
// el registro del controlador; la conducta, que es lo que hay que verificar, vive en este módulo.

// Retardo del debounce de las comprobaciones contra el servidor. La TC pide feedback en menos de
// 500 ms: con los 500 ms de espera anteriores el veredicto llegaba siempre por encima del listón
// (500 ms de debounce + ida y vuelta de red). 300 ms es el extremo bajo de la banda 300-500 ms que
// fija US-134-1.1.4 para no saturar el endpoint de disponibilidad.
export const RETARDO_DE_COMPROBACION = 300

// Confirmación de que el nombre está libre. Ya NO se escribe bajo el campo: el veredicto visible es
// el ✓ que `access.scss` pinta dentro del propio campo, como el del correo (rankia#11306) y como lo
// rotula la maqueta (decisión de Producto, 2026-08-05). Bajo el campo no queda nada escrito, así que
// esta frase sobrevive solo para lectores de pantalla —el color y el icono no pueden ser los únicos
// portadores del significado (WCAG 1.4.1)—. No la manda el servidor: el endpoint responde 200 con
// cuerpo vacío y no queremos meterle presentación.
export const MENSAJE_NICK_DISPONIBLE = 'Disponible'

// Marca de campo válido. La pone el controlador y la dibuja `access.scss`: borde verde y ✓ dentro
// del campo. La comparten el nombre de Rankiano, el correo y la contraseña, pero solo los dos
// primeros pintan ✓ —la contraseña lo tiene desactivado a propósito, porque los tres ✓ de su
// checklist ya lo dicen (decisión de Producto 2026-08-04, TC-134-1.1.6.1).
export const CLASE_CAMPO_VALIDO = 'rnk-Forms_Text-success'

export const MENSAJE_NICK_MAL_FORMADO =
  'El nombre de usuario solo puede contener letras sin acentos, números, guion bajo (_) y guion (-)'

const LONGITUD_MINIMA_DEL_NICK = 5
const LONGITUD_MAXIMA_DEL_NICK = 20

// Requisitos de la contraseña del alta (RF-7 de PRD-134-1: 8 caracteres, una mayúscula y un
// número). Las claves casan con el `data-requisito` de cada `<li>` de la checklist y las
// expresiones son las mismas que valida `UsuarioDeRegistro::REQUISITOS_PASSWORD` en servidor.
// El tope de 200 de `longitud` no es de RF-7 sino de la longitud 5–200 que `Usuario` sigue
// validando: sin él, una contraseña de más de 200 caracteres pintaba los tres requisitos en verde
// y el envío lo rechazaba luego el `pattern` del campo con un aviso que la contraseña sí cumplía.
// TC-134-1.1.6.1.
export const REQUISITOS_PASSWORD = {
  longitud: /^.{8,200}$/,
  mayuscula: /\p{Lu}/u,
  numero: /[0-9]/
}

// Los dos estados que puede dibujar la marca de un campo. Son claves, no marcado: el icono es un
// SVG del repositorio de iconos (`icons/info-outline.svg` e `icons/check-outline.svg`) que la vista pinta
// una sola vez con `inline_svg_tag` dentro de un `<template>`
// (`usuarios/registro/_plantillas_de_iconos`) y que este controlador clona.
//
// Eran los caracteres tipográficos `ⓘ` y `✓`, que dibujaban una "i" más fina y pequeña que la del
// aviso de permanencia justo debajo del campo. Y el XML del SVG no puede vivir aquí: sería una
// segunda copia del icono, libre de divergir del asset del repositorio — el mismo problema, un
// paso más allá. TC-134-1.1.4.4.
export const ICONO_REQUISITO_CUMPLIDO = 'cumplido'
export const ICONO_REQUISITO_PENDIENTE = 'pendiente'
// El color no puede ser el único portador del significado (WCAG 1.4.1), así que además del icono
// cada requisito lleva su estado en texto, oculto visualmente y disponible para lectores.
//
// Coinciden en valor con las claves de arriba por casualidad del idioma, pero no son lo mismo: esto
// es copy que se anuncia y aquello es una clave interna. Si el copy cambia, cambia solo aquí.
export const TEXTO_REQUISITO_CUMPLIDO = 'cumplido'
export const TEXTO_REQUISITO_PENDIENTE = 'pendiente'

// Tamaño del icono que precede a los mensajes de error del campo de correo, que llega por el
// mecanismo del value `iconoDeError` (máscara de `svg_pseudo_icon`) y no por las plantillas de
// arriba — ver `conIconoDeError`. TC-134-1.1.6.3.
const TAMANO_ICONO_ERROR = '13px'

// Códigos con los que los endpoints de comprobación dan un VEREDICTO sobre lo escrito. Cualquier
// otro (el 429 del rate limit, un 5xx) es un fallo de la comprobación, no un veredicto: pintarlo en
// rojo es lo que ya hacía el formulario, pero contarlo en la analítica del embudo diría que el
// dominio no existe o que el nombre está cogido cuando nadie lo ha comprobado. TC-134-1.4.5.3.
const ESTADO_DOMINIO_RECHAZADO = 422
const ESTADO_NOMBRE_RECHAZADO = 409

// Indicador de fortaleza orientativo (TC-134-1.1.6.2). El motor (pack `password_strength`)
// devuelve el score entero 0-4 de zxcvbn y aquí se traduce a los tres niveles que ve el usuario,
// con el mapeo que fija la nota técnica de US-134-1.1.6: 0-1 → Débil, 2 → Media, 3-4 → Fuerte.
// El índice del array ES el score, así que la tabla se lee de un vistazo.
//
// El nivel viaja al DOM como `data-nivel` del contenedor y es el CSS quien colorea los segmentos
// de la barra a partir de él: el JavaScript no toca estilos.
const FORTALEZA_POR_SCORE = [
  { nivel: '1', etiqueta: 'Débil' },
  { nivel: '1', etiqueta: 'Débil' },
  { nivel: '2', etiqueta: 'Media' },
  { nivel: '3', etiqueta: 'Fuerte' },
  { nivel: '3', etiqueta: 'Fuerte' }
]

export default class RegistroController extends Controller {
  static targets = [
    'inputNick', 'inputEmail', 'inputPwd', 'submitButton',
    'requisitosPwd', 'captionPwd', 'listaRequisitosPwd', 'requisitoPwd',
    'fortalezaPwd', 'etiquetaFortalezaPwd',
    'plantillaIconoPendiente', 'plantillaIconoCumplido'
  ]

  // El icono que precede a los mensajes de error del correo solo se pinta donde la vista lo
  // declara con `data-registro-icono-de-error-value` (`usuarios/registro/_campos_registro`), que
  // lleva ya la URL del SVG resuelta por el pipeline. El otro formulario que comparte este
  // controlador —`ponencia/pago/_datos_de_acceso`— no tiene ese lenguaje visual, no declara el
  // value y sigue mostrando sus mensajes tal cual. Los mensajes del nombre de Rankiano no lo usan:
  // su marca sale de las plantillas (`svgDelIcono`). TC-134-1.1.6.3.
  static values = { iconoDeError: String }

  connect () {
    this.timer = {}
    this.timerChecker = RETARDO_DE_COMPROBACION
    // Sello de la última comprobación lanzada sobre cada campo — ver `chequeaAjax`.
    this.sellos = new Map()
    // El botón solo se habilita con un nombre válido y libre (TC-134-1.1.4.4), así que arranca
    // bloqueado. Se bloquea desde JS y no desde el HTML a propósito: si el pack no llega a
    // ejecutarse, el formulario sigue siendo enviable y lo valida el servidor, como hasta ahora.
    this.nickAceptado = false
    this.enviando = false

    // Errores que la interfaz pinta MIENTRAS se teclea (formato del correo, formato del nombre,
    // contraseña que aún no cumple). No se emiten al pintarlos: durante el tecleo "todavía no vale"
    // es el estado normal de cualquier campo y `signup_field_error` saldría en el 100 % de las
    // sesiones, que no mide nada. Se emiten al SALIR del campo — ahí ya es un error de verdad—,
    // desde `campoDesenfocado`. Los que llegan de una comprobación terminada (dominio inexistente,
    // nombre en uso) sí se emiten en el acto: ahí no hay nada a medias. TC-134-1.4.5.3.
    this.erroresPendientes = new Map()

    this.boundDisableSubmit = this.disableSubmit.bind(this)
    this.boundActualizaBotonDeEnvio = this.actualizaBotonDeEnvio.bind(this)
    this.boundCampoEnfocado = this.campoEnfocado.bind(this)
    this.boundCampoDesenfocado = this.campoDesenfocado.bind(this)
    this.element.addEventListener('submit', this.boundDisableSubmit)
    this.boundEnvioTerminado = this.envioTerminado.bind(this)
    this.element.addEventListener('turbo:submit-end', this.boundEnvioTerminado)
    // `focusin`/`focusout` y no `focus`/`blur` porque estos no burbujean: con los dos primeros
    // basta un par de escuchas en el formulario y ningún campo necesita declarar su propia acción.
    this.element.addEventListener('focusin', this.boundCampoEnfocado)
    this.element.addEventListener('focusout', this.boundCampoDesenfocado)
    // El estado del botón depende de todo el formulario, no solo de los campos con acción propia
    // (la casilla legal no la tiene). `focusout` cubre además los rellenos que no emiten `input`,
    // como algunos autocompletados del navegador.
    this.element.addEventListener('input', this.boundActualizaBotonDeEnvio)
    this.element.addEventListener('change', this.boundActualizaBotonDeEnvio)
    this.element.addEventListener('focusout', this.boundActualizaBotonDeEnvio)

    // El campo puede llegar relleno: re-render del formulario con errores de servidor, con lo que
    // la persona había escrito. Sin esta comprobación el botón quedaría bloqueado hasta que
    // tocase un campo que ya estaba bien.
    if (this.hasInputNickTarget && this.inputNickTarget.value) this.chequeaNick()

    this.actualizaBotonDeEnvio()
  }

  disconnect () {
    this.element.removeEventListener('submit', this.boundDisableSubmit)
    this.element.removeEventListener('turbo:submit-end', this.boundEnvioTerminado)
    this.element.removeEventListener('focusin', this.boundCampoEnfocado)
    this.element.removeEventListener('focusout', this.boundCampoDesenfocado)
    this.element.removeEventListener('input', this.boundActualizaBotonDeEnvio)
    this.element.removeEventListener('change', this.boundActualizaBotonDeEnvio)
    this.element.removeEventListener('focusout', this.boundActualizaBotonDeEnvio)
    Object.values(this.timer).forEach(clearTimeout)
  }

  disableSubmit (evento) {
    // Turnstile cancela el envío mientras no haya resuelto el reto (`turnstile_controller`, que
    // escucha antes). Ese envío no ha salido: ni cuenta en el embudo ni bloquea el botón.
    if (evento?.defaultPrevented) return

    // El navegador no deja llegar aquí un formulario inválido, así que este es el envío de verdad:
    // el paso "Enviar formulario" del embudo. Va antes del guard del botón para que se emita
    // también donde el formulario no tenga botón declarado como target. TC-134-1.4.5.3.
    emiteEnvioDelFormulario({ method: METODO_EMAIL })

    if (!this.hasSubmitButtonTarget) return

    this.enviando = true
    this.textoDelBoton = this.submitButtonTarget.value
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.value = this.submitButtonTarget.dataset.disableText || 'Registrando...'
  }

  // Si el servidor rechaza el envío sin re-pintar la página —el Turbo Stream de
  // `CloudflareTurnstileVerificable`, que solo cambia el aviso—, el formulario sigue ahí con el botón
  // en "Registrando..." y nada más que hacer. Se devuelve al estado de antes para poder reintentar.
  envioTerminado (evento) {
    if (evento.detail?.success || !this.enviando || !this.hasSubmitButtonTarget) return

    this.enviando = false
    this.submitButtonTarget.value = this.textoDelBoton
    this.actualizaBotonDeEnvio()
  }

  // El botón de crear cuenta solo se activa con un nombre de Rankiano válido y disponible, y con el
  // resto del formulario en regla (US-134-1.1.4: "el botón se activa (si el resto de campos también
  // es válido)"). Se lee `validity` campo a campo en lugar de `form.checkValidity()` para no emitir
  // eventos `invalid` en cada pulsación.
  actualizaBotonDeEnvio () {
    if (!this.hasSubmitButtonTarget || this.enviando) return

    this.submitButtonTarget.disabled = !(this.nickAceptado && this.restoDelFormularioValido())
  }

  restoDelFormularioValido () {
    return Array.from(this.element.elements)
      .every(campo => !campo.willValidate || campo.validity.valid)
  }

  // --- Analítica del embudo de onboarding (TC-134-1.4.5.3) --------------------------------------
  //
  // El controlador no emite nada por su cuenta: llama al módulo `lib/analitica_del_registro`, que
  // solo habla cuando la pantalla ha abierto sesión con `signup_view`. Por eso el otro formulario
  // que comparte este controlador (`ponencia/pago/_datos_de_acceso`) no emite ni un evento sin
  // ningún condicional aquí dentro.

  // Primer foco de cada campo, y elección del camino de correo: tocar el formulario ES elegirlo,
  // frente al botón de Google que está fuera de él.
  campoEnfocado (evento) {
    const campo = this.campoDeAnalitica(evento.target)
    if (!campo) return

    // Elegir el camino de correo es empezar a rellenarlo. La casilla legal no cuenta: se marca al
    // final y tabular hasta ella no es haber tocado el formulario de correo y contraseña.
    if (campo !== CAMPO_TERMINOS) emiteMetodoElegido(METODO_EMAIL)
    emiteCampoEnfocado(campo)
  }

  // Al salir del campo se cobra el error que la interfaz venía pintando en vivo. La casilla legal
  // no pinta nada mientras se rellena, así que su veredicto se resuelve aquí mismo.
  campoDesenfocado (evento) {
    const campo = this.campoDeAnalitica(evento.target)
    if (!campo) return

    if (campo === CAMPO_TERMINOS) {
      return evento.target.checked
        ? limpiaErrorDeCampo(campo)
        : emiteErrorDeCampo(campo, ERROR_TERMINOS)
    }

    const pendiente = this.erroresPendientes.get(campo)
    if (pendiente) emiteErrorDeCampo(campo, pendiente)
  }

  // Nombre del campo en el vocabulario del embudo, o `null` si el elemento no es uno de los campos
  // que mide la TC (el botón de envío, el de mostrar contraseña...).
  campoDeAnalitica (elemento) {
    if (this.hasInputEmailTarget && elemento === this.inputEmailTarget) return CAMPO_EMAIL
    if (this.hasInputNickTarget && elemento === this.inputNickTarget) return CAMPO_NOMBRE
    if (this.hasInputPwdTarget && elemento === this.inputPwdTarget) return CAMPO_PASSWORD
    if (/terminos_condiciones/.test(elemento.name || elemento.id || '')) return CAMPO_TERMINOS

    return null
  }

  anotaErrorPendiente (campo, tipo) {
    this.erroresPendientes.set(campo, tipo)
  }

  // El campo ha dejado de estar en error: ni queda nada pendiente de cobrar al salir, ni sigue
  // vigente lo ya emitido — si el mismo error reaparece, vuelve a contar.
  resuelveCampo (campo) {
    this.erroresPendientes.delete(campo)
    limpiaErrorDeCampo(campo)
  }

  // `icono` es opcional: quien lo pasa (hoy el nombre de usuario) pinta el mensaje con la marca de
  // estado colgada a la izquierda del texto; quien no, conserva el mensaje en texto plano de
  // siempre — o, en el caso del correo, con el icono ya incrustado por `conIconoDeError`.
  notifySet (input, error, color, message, icono) {
    input.style.color = color
    input.style.borderColor = color
    error.style.color = color
    error.classList.remove('rnk-Icon-spinner-inline')

    if (icono && message) {
      error.replaceChildren(this.lineaDeEstado(icono, message))
    } else {
      error.innerHTML = message || ''
    }
  }

  // Icono y texto en elementos hermanos dentro del `%small`, con las clases que `access.scss`
  // coloca en flex: así, cuando el mensaje ocupa dos líneas, el icono queda fuera del bloque de
  // texto (sangría francesa) en lugar de empujarlo, que es como lo pinta la maqueta y como ya se
  // ve la checklist de la contraseña.
  lineaDeEstado (icono, mensaje) {
    const linea = document.createElement('span')
    linea.className = 'rnk-FieldStatus'

    const marca = document.createElement('span')
    marca.className = 'rnk-FieldStatus_Icon'
    // El icono es decorativo: el mensaje de al lado ya dice qué pasa, y un lector de pantalla que
    // lo leyera anunciaría ruido ("círculo con i") antes de la frase.
    marca.setAttribute('aria-hidden', 'true')
    this.pintaIcono(marca, icono)

    const texto = document.createElement('span')
    texto.className = 'rnk-FieldStatus_Text'
    texto.textContent = mensaje

    // El espacio entre ambos lo descarta flex (los nodos de solo espacios no son ítems), y salva la
    // legibilidad donde esta hoja no llega: el mismo controlador pinta el campo en el checkout de
    // ponencias (`ponencia/pago/_datos_de_acceso`), que no carga `access.scss`.
    linea.append(marca, ' ', texto)

    return linea
  }

  // Deja en `elemento` el icono del estado pedido. El estado va además en `data-icono`: así se lee
  // en el DOM sin depender de qué dibujo lleve dentro, que es lo que antes hacía el propio
  // carácter cuando la marca era `ⓘ` o `✓`.
  pintaIcono (elemento, icono) {
    elemento.dataset.icono = icono

    const svg = this.svgDelIcono(icono)
    elemento.replaceChildren(...(svg ? [svg] : []))
  }

  // Clona el SVG que la vista ha dejado en el `<template>` del estado pedido
  // (`usuarios/registro/_plantillas_de_iconos`). Devuelve `null` si el formulario no las renderiza:
  // entonces el mensaje va sin icono, que es decorativo — el texto de al lado sigue diciendo lo
  // mismo. Clonar la plantilla, y no construir el SVG aquí, deja el asset del repositorio como
  // único origen del icono.
  svgDelIcono (icono) {
    const esCumplido = icono === ICONO_REQUISITO_CUMPLIDO
    const hayPlantilla = esCumplido
      ? this.hasPlantillaIconoCumplidoTarget
      : this.hasPlantillaIconoPendienteTarget

    if (!hayPlantilla) return null

    const plantilla = esCumplido ? this.plantillaIconoCumplidoTarget : this.plantillaIconoPendienteTarget
    const svg = plantilla.content.firstElementChild

    return svg ? svg.cloneNode(true) : null
  }

  // Los mensajes del formulario llegan de dos sitios con puntuación distinta: los `errormsg` de la
  // vista vienen sin punto («…entre 5 y 20 caracteres») y los que responde el servidor ya lo traen
  // («…esté bien escrito.»). Añadirlo a ciegas dejaba el doble punto en el globo nativo del
  // navegador, que es donde se lee esta cadena. TC-134-1.1.6.3.
  conPuntoFinal (message) {
    return /[.!?]$/.test(message) ? message : message + '.'
  }

  // `mensajeVisible` se separa de `message` para poder anteponerle el icono de estado sin meterlo
  // en la validez nativa del campo, que la lee el navegador y no entiende de iconos.
  notify (success, input, error, color, message, icono, mensajeVisible = message) {
    input.setCustomValidity(success ? '' : this.conPuntoFinal(message))
    this.notifySet(input, error, color, mensajeVisible, icono)
    // La validez del campo acaba de cambiar. El botón de envío depende de la validez de TODO el
    // formulario, así que hay que recalcularlo aquí y no solo en los eventos de la persona: cuando
    // el cambio llega de forma asíncrona —la respuesta de una comprobación— ya no queda ningún
    // evento detrás que lo recalcule, y el botón se quedaría bloqueado para siempre. TC-134-1.1.4.4.
    this.actualizaBotonDeEnvio()
  }

  conIconoDeError (message) {
    if (!this.iconoDeErrorValue || !message) return message

    // Decorativo: el significado lo porta el mensaje de al lado, no el icono (WCAG 1.4.1).
    // `rnk-svg-icon-pseudo` es el mismo mecanismo que emite el helper `svg_pseudo_icon` en la
    // vista: máscara con la URL del SVG, pintada con `currentColor` — aquí, el rojo del mensaje.
    const estilo = `--icon-url: url(${this.iconoDeErrorValue}); --icon-size: ${TAMANO_ICONO_ERROR}`
    const atributos = `class="rnk-svg-icon-pseudo rnk-Forms_StateIcon" aria-hidden="true" style="${estilo}"`

    return `<span ${atributos}></span>${message}`
  }

  notifyWait (error) {
    error.classList.add('rnk-Icon-spinner-inline')
    error.innerHTML = '&nbspComprobando...'
    error.style.color = 'inherit'
  }

  // Dos mecanismos de marca conviven a propósito: con `icono` (el nombre de Rankiano) la marca sale
  // de las plantillas de la vista y se cuelga en su propio elemento (`lineaDeEstado`); sin él, el
  // mensaje va tal cual salvo que la vista declare `iconoDeError`, que es como el correo antepone
  // su ⓘ (TC-134-1.1.6.3, rankia#11306).
  notifyError (input, error, message, icono) {
    input.classList.remove(CLASE_CAMPO_VALIDO)
    this.notify(false, input, error, '#e91101', message, icono,
      icono ? message : this.conIconoDeError(message))
  }

  // Sin `icono`: ningún campo cuelga marca del mensaje al acertar. El nombre de Rankiano lo dice
  // con el ✓ de dentro del campo (`marcaNickDisponible`) y la contraseña con los ✓ de su checklist.
  //
  // El email no afirma nada por el mero formato: su estado de acierto solo lo pinta
  // `marcaEmailValido`, con el dominio ya comprobado. Antes esta rama dependía de
  // `checkValidity()` y, al recuperarse de un error, colaba un «Email válido» transitorio que
  // ningún chequeo de dominio respaldaba. TC-134-1.1.6.3.
  notifySuccess (input, error, message) {
    if (input.type === 'email') {
      this.notify(true, input, error, '', '')
    } else {
      this.notify(true, input, error, '#00856f', message)
    }
  }

  // Un campo puede tener dos comprobaciones en vuelo a la vez —la de la última tecla y la del
  // blur— y las respuestas no vuelven necesariamente en el mismo orden en que salieron. El sello
  // hace que solo pinte la respuesta de la última comprobación lanzada sobre ese campo
  // (TC-134-1.1.6.3); el valor consultado descarta además la respuesta de una pulsación anterior
  // cuando el camino de error no llega a lanzar comprobación nueva (TC-134-1.1.4.4).
  chequeaAjax (options) {
    const valorConsultado = options.input.value
    const sello = (this.sellos.get(options.input) || 0) + 1
    this.sellos.set(options.input, sello)

    const esVigente = () =>
      this.sellos.get(options.input) === sello && options.input.value === valorConsultado

    fetch(options.url, { credentials: 'same-origin' })
      .then(response => response.json().then(body => ({ ok: response.ok, status: response.status, body })))
      .then(({ ok, status, body }) => {
        if (!esVigente()) return

        if (ok) {
          // `??` y no `||`: un `mensajeDeExito` vacío es una decisión —el nombre de Rankiano no
          // escribe nada bajo el campo al acertar— y no debe caer al mensaje del servidor.
          this.notifySuccess(options.input, options.error, options.mensajeDeExito ?? body.message)
          if (options.alAcertar) options.alAcertar()
        } else {
          // Los mensajes que llegan del servidor —el nombre en uso, el prefijo `rank` reservado—
          // se pintan con la misma marca de estado que los que resuelve el navegador.
          this.notifyError(options.input, options.error, body['full_message'] || body.message, options.iconoDeError)
          // El veredicto en clave del servidor (`code`), no el mensaje: el mensaje es copy y puede
          // cambiar sin avisar a la analítica. TC-134-1.4.5.3.
          if (options.alFallar) options.alFallar(body, status)
        }

        if (options.alResolver) options.alResolver(ok)

        // Última palabra del estado del botón para esta comprobación: quede el campo como quede,
        // el recálculo no puede depender de qué rama lo pintó.
        this.actualizaBotonDeEnvio()
      })
      .catch(() => {
        // No se ha podido comprobar (red caída, respuesta ilegible). No bloqueamos el alta por
        // ello: se limpia el "Comprobando..." y el veredicto lo da el servidor al enviar. Se pasa
        // por `notify` y no por `notifySet` para levantar también el `setCustomValidity` que
        // pudiera venir de un error anterior del mismo campo; si no, el campo seguiría inválido
        // y el botón bloqueado justo cuando hemos decidido no bloquear.
        if (!esVigente()) return

        this.notify(true, options.input, options.error, '', '')
        if (options.alResolver) options.alResolver(true)
        this.actualizaBotonDeEnvio()
      })
  }

  chequeando (options, field) {
    this.notifyWait(options.chequeaAjaxOptions.error)
    clearTimeout(this.timer[field])
    this.timer[field] = setTimeout(() => { this.chequeaAjax(options.chequeaAjaxOptions) }, this.timerChecker)
  }

  chequeaNick () {
    const input = this.inputNickTarget
    const error = input.nextElementSibling
    const valor = input.value

    // Mientras no haya veredicto para el valor actual, el nombre no está aceptado: cada pulsación
    // invalida la comprobación anterior. Y con ella se retira el ✓ de dentro del campo: lo que hay
    // escrito ahora todavía no lo ha aprobado nadie.
    this.nickAceptado = false
    input.classList.remove(CLASE_CAMPO_VALIDO)

    // La comprobación pendiente se cancela aquí y no solo al programar la siguiente: los caminos de
    // error de abajo no llegan a `chequeando`, así que un debounce lanzado por un valor válido
    // anterior seguiría vivo. Al dispararse, `chequeaAjax` lee el valor del campo *en ese momento*
    // para decidir si la respuesta es suya, y como el campo ya no cambia después, la respuesta de
    // "lucas" se daba por buena para "luca": ✓ verde sobre un valor que se acababa de rechazar.
    clearTimeout(this.timer.nick)

    if (valor.length < LONGITUD_MINIMA_DEL_NICK || valor.length > LONGITUD_MAXIMA_DEL_NICK) {
      const rango = `${LONGITUD_MINIMA_DEL_NICK} y ${LONGITUD_MAXIMA_DEL_NICK}`
      const mensaje = `El nombre de usuario debe tener entre ${rango} caracteres`
      this.notifyError(input, error, mensaje, ICONO_REQUISITO_PENDIENTE)
      this.rechazaElFormatoDelNombre(valor)
    } else if (!this.cumpleElPatron(input)) {
      this.notifyError(input, error, MENSAJE_NICK_MAL_FORMADO, ICONO_REQUISITO_PENDIENTE)
      this.rechazaElFormatoDelNombre(valor)
    } else {
      // Mismo motivo que en `chequeaEmail`: el nombre ya tiene formato válido, así que el error de
      // formato de las pulsaciones anteriores no puede cobrarse si se sale del campo antes del
      // veredicto del servidor. TC-134-1.4.5.3.
      this.erroresPendientes.delete(CAMPO_NOMBRE)
      this.chequeando({
        chequeaAjaxOptions: {
          url: '/usuario/comprueba-nick?nick=' + encodeURIComponent(valor),
          input: input,
          error: error,
          // Nada escrito bajo el campo al acertar: el veredicto es el ✓ de dentro (`alAcertar`).
          mensajeDeExito: '',
          iconoDeError: ICONO_REQUISITO_PENDIENTE,
          // Solo en el acierto real del servidor. El fallo de red también deja pasar el nombre
          // (`alResolver(true)` desde el `catch`), pero eso no es un "disponible" comprobado y no
          // puede pintar el ✓: ahí el veredicto lo da el servidor al enviar.
          alAcertar: () => {
            this.marcaNickDisponible()
            this.resuelveCampo(CAMPO_NOMBRE)
            emiteResultadoDelNombre(RESULTADO_DISPONIBLE, { deServidor: true })
          },
          alFallar: (cuerpo, estado) => this.rechazaElNombreDesdeElServidor(cuerpo, estado),
          alResolver: disponible => {
            this.nickAceptado = disponible
            this.actualizaBotonDeEnvio()
          }
        }
      }, 'nick')
    }

    this.actualizaBotonDeEnvio()
  }

  // Veredicto de FORMATO del nombre, el que resuelve el navegador en cada pulsación. El campo vacío
  // no es un veredicto: es un campo sin rellenar, y contarlo metería un `invalid_format` en cuanto
  // alguien borra lo que había escrito. TC-134-1.4.5.3.
  rechazaElFormatoDelNombre (valor) {
    if (valor === '') return this.resuelveCampo(CAMPO_NOMBRE)

    this.anotaErrorPendiente(CAMPO_NOMBRE, ERROR_NOMBRE_MAL_FORMADO)
    emiteResultadoDelNombre(RESULTADO_MAL_FORMADO)
  }

  // Veredicto del SERVIDOR: el nombre está cogido o es de los reservados por marca. Llega con una
  // comprobación terminada, así que el error se emite en el acto y no espera a salir del campo.
  rechazaElNombreDesdeElServidor (cuerpo, estado) {
    if (estado !== ESTADO_NOMBRE_RECHAZADO) return

    const enUso = (cuerpo || {}).code === 'taken'

    this.erroresPendientes.delete(CAMPO_NOMBRE)
    emiteErrorDeCampo(CAMPO_NOMBRE, enUso ? ERROR_NOMBRE_EN_USO : ERROR_NOMBRE_MAL_FORMADO)
    emiteResultadoDelNombre(enUso ? RESULTADO_EN_USO : RESULTADO_MAL_FORMADO, { deServidor: true })
  }

  // Nombre libre: el veredicto se ve dentro del campo —✓ verde a la derecha, que dibuja
  // `access.scss` a partir de esta clase—, no como una frase debajo. Es lo que rotula la maqueta y
  // el mismo lenguaje que el campo de correo (rankia#11306). Decisión de Producto, 2026-08-05.
  //
  // Debajo del campo no queda nada visible, así que la confirmación se anuncia solo para lectores
  // de pantalla, dentro del `aria-live` que ya envuelve la zona de mensajes: sin ella el acierto lo
  // portarían únicamente el color y el icono, que es justo lo que WCAG 1.4.1 no admite.
  marcaNickDisponible () {
    const input = this.inputNickTarget

    input.classList.add(CLASE_CAMPO_VALIDO)
    input.nextElementSibling.replaceChildren(this.soloParaLectores(MENSAJE_NICK_DISPONIBLE))
  }

  soloParaLectores (mensaje) {
    const texto = document.createElement('span')
    texto.className = 'rnk-Forms_Label-sr-only'
    texto.textContent = mensaje

    return texto
  }

  // El atributo `pattern` de HTML se evalúa anclado a toda la cadena; `new RegExp(pattern)` no.
  // Sin anclar, `[A-Za-z0-9_-]{5,20}` daba por bueno cualquier valor que contuviera cinco
  // caracteres permitidos seguidos, así que "jose lopez", "josé_lopez" o "jose.lopez" pasaban el
  // filtro del cliente y se iban al endpoint de disponibilidad. TC-134-1.1.4.4.
  //
  // Lo usan todos los campos que replican aquí su `pattern`, y no solo el nombre de Rankiano: el
  // patrón del correo (`Email::INPUT_PATTERN`) trae sus propios `^`/`$` dentro de la cadena y por
  // eso no arrastraba el fallo, pero dejar ahí la forma sin anclar es la misma trampa esperando a
  // un patrón que no venga anclado desde el modelo.
  cumpleElPatron (input) {
    if (!input.pattern) return true

    return new RegExp(`^(?:${input.pattern})$`).test(input.value)
  }

  // Formato y dominio se resuelven en la misma pasada mientras se escribe, y las dos respuestas se
  // pintan igual: el ✓ si la dirección está bien y el rojo con su mensaje si el dominio no existe,
  // sin esperar a que el usuario salga del campo. `test@gmail.comda` es un email bien formado, así
  // que el formato calla y el único que puede avisar del typo es el dominio: si ese aviso espera al
  // blur, el campo se queda sin decir nada justo cuando el usuario está mirando lo que ha escrito.
  //
  // No se pregunta por pulsación —el `setTimeout` de `chequeando` solo dispara cuando se deja de
  // teclear— y el veredicto va cacheado por dominio en servidor, así que un email tecleado del
  // tirón gasta una consulta DNS, no una por letra. TC-134-1.1.6.3.
  chequeaEmail () {
    const input = this.inputEmailTarget
    const error = input.nextElementSibling

    // Cada tecla retira el ✓: lo que hay escrito ahora nadie ha comprobado todavía que exista.
    // Vuelve a aparecer en cuanto la comprobación confirme el nuevo valor.
    input.classList.remove(CLASE_CAMPO_VALIDO)

    // Mismo motivo que en `chequeaNick`: el camino de error no pasa por `chequeando`, así que sin
    // esto un debounce de un valor válido anterior sobreviviría al veredicto que se pinta ahora.
    clearTimeout(this.timer.email)

    if (!this.cumpleElPatron(input)) {
      this.notifyError(input, error, input.dataset.errormsg)
      // El formato lo resuelve el navegador en cada pulsación: se cobra al salir del campo. Con el
      // campo vacío no hay error que cobrar, solo un campo sin rellenar. TC-134-1.4.5.3.
      if (input.value === '') this.resuelveCampo(CAMPO_EMAIL)
      else this.anotaErrorPendiente(CAMPO_EMAIL, ERROR_FORMATO)
    } else {
      // El valor ya cumple el patrón: el error de formato que dejó el tecleo anterior ya no es de
      // este valor y no puede cobrarse al salir del campo mientras el servidor aún no ha contestado
      // (todo correo pasa por `inv@` antes de estar completo). TC-134-1.4.5.3.
      this.erroresPendientes.delete(CAMPO_EMAIL)
      this.chequeando({ chequeaAjaxOptions: this.comprobacionDeEmail() }, 'email')
    }
  }

  // Al perder el foco se repite la comprobación: cubre salir del campo antes de que salte el chequeo
  // de la última tecla —pegar la dirección y tabular, sin pausa que dispare el `setTimeout`—, que si
  // no dejaría el campo sin veredicto. El aviso usa el mismo `notifyError` que el resto de campos y
  // no propone corrección (decisión de Producto: nada de "¿quisiste decir gmail.com?").
  chequeaDominioEmail () {
    // Con el campo vacío o con un email mal formado no hay dominio que resolver: de ese aviso ya se
    // encarga `chequeaEmail` mientras se escribe.
    if (!this.cumpleElPatron(this.inputEmailTarget)) return

    // El chequeo por tecla deja un timer pendiente que llegaría después de este y repetiría la
    // consulta. Se cancela antes de preguntar.
    clearTimeout(this.timer.email)

    this.chequeaAjax(this.comprobacionDeEmail())
  }

  comprobacionDeEmail () {
    return {
      url: '/usuario/comprueba-email?comprueba_dominio=1&email=' + encodeURIComponent(this.inputEmailTarget.value),
      input: this.inputEmailTarget,
      error: this.inputEmailTarget.nextElementSibling,
      alAcertar: () => {
        this.marcaEmailValido()
        this.resuelveCampo(CAMPO_EMAIL)
        // Si antes hubo aviso de dominio, esto es la corrección: es lo que cuenta
        // `fixed_domain_after_warning` en `signup_form_submitted`. TC-134-1.4.5.3.
        marcaDominioComprobado()
      },
      // El 422 de este endpoint solo lo produce un dominio que no resuelve, así que el veredicto
      // es inequívoco y se emite en el acto. El 429 del rate limit y cualquier 5xx no dicen nada
      // del correo escrito: se pintan igual, pero no cuentan como aviso de dominio.
      alFallar: (_cuerpo, estado) => {
        if (estado !== ESTADO_DOMINIO_RECHAZADO) return

        this.erroresPendientes.delete(CAMPO_EMAIL)
        marcaAvisoDeDominio()
        emiteErrorDeCampo(CAMPO_EMAIL, ERROR_DOMINIO_DESCONOCIDO)
      }
    }
  }

  // Estado de acierto del campo Email (maqueta "Registro - Success"): borde verde y ✓ dentro del
  // campo, sin texto debajo — el ✓ es todo el mensaje (decisión de Producto 2026-08-05: con el
  // check dentro del campo, un «Email válido» debajo es redundante). Se pinta solo aquí, con el
  // dominio ya resuelto: dar por bueno en verde un correo con typo, solo porque tenga forma de
  // email, es justo el falso «Email válido» que esta TC viene a quitar (H5.2).
  marcaEmailValido () {
    this.inputEmailTarget.classList.add(CLASE_CAMPO_VALIDO)
    // El verde del borde y del texto lo pone la clase; esto retira cualquier pintado en línea
    // anterior. `notifySuccess` ya ha limpiado la validez nativa y el texto del `small`.
    this.notifySet(this.inputEmailTarget, this.inputEmailTarget.nextElementSibling, '')
  }

  chequeaPwd () {
    // La checklist viva solo se activa donde la vista declara sus targets, es decir en /registro
    // (`usuarios/registro/_campos_registro`). El otro formulario que comparte este controlador
    // —`ponencia/pago/_datos_de_acceso`— no los declara y conserva la política 5–200 de abajo.
    // TC-134-1.1.6.1.
    if (this.hasRequisitosPwdTarget) return this.chequeaPwdConChecklist()

    const input = this.inputPwdTarget
    const error = input.nextElementSibling

    if (input.value.length < 5 || input.value.length > 200) {
      this.notifyError(input, error, 'La contraseña debe tener entre 5 y 200 caracteres')
    } else {
      this.notifySuccess(input, error, '')
    }
  }

  // La checklist ya se ve desde que carga la página (la vista no la oculta); el foco la repinta, y
  // la quita del `hidden` si una vista la trae oculta. Con el campo vacío solo se ve el caption
  // neutro unificado; la lista de los tres requisitos no aparece hasta el primer carácter.
  muestraRequisitosPwd () {
    if (!this.hasRequisitosPwdTarget) return

    this.requisitosPwdTarget.hidden = false
    this.chequeaPwdConChecklist()
  }

  chequeaPwdConChecklist () {
    const valor = this.inputPwdTarget.value
    const haEmpezadoAEscribir = valor.length > 0

    this.requisitosPwdTarget.hidden = false
    this.captionPwdTarget.hidden = haEmpezadoAEscribir
    this.listaRequisitosPwdTarget.hidden = !haEmpezadoAEscribir

    const cumplidos = this.requisitoPwdTargets.map(item => this.pintaRequisitoPwd(item, valor))
    this.marcaEstadoDelCampoPwd(haEmpezadoAEscribir, cumplidos.every(Boolean))
    this.pintaFortalezaPwd(valor)
  }

  pintaRequisitoPwd (item, valor) {
    const cumplido = REQUISITOS_PASSWORD[item.dataset.requisito].test(valor)

    item.classList.toggle('rnk-PasswordChecklist_Item-cumplido', cumplido)
    item.dataset.cumplido = cumplido
    this.pintaIcono(
      item.querySelector('[data-icono]'),
      cumplido ? ICONO_REQUISITO_CUMPLIDO : ICONO_REQUISITO_PENDIENTE
    )
    item.querySelector('[data-estado]').textContent =
      cumplido ? TEXTO_REQUISITO_CUMPLIDO : TEXTO_REQUISITO_PENDIENTE

    return cumplido
  }

  // El estado success reutiliza el lenguaje visual que este controlador ya aplica al resto de
  // campos: solo el borde verde vía `notifySuccess`. No lleva ✓ dentro del campo — los tres ✓ de
  // la checklist ya comunican que la contraseña es válida (decisión de Producto 2026-08-04).
  // El mensaje visible de qué falta lo lleva la checklist, así que en el error se usa `notifySet`
  // (solo color, sin texto) en vez de `notifyError`, y la validez nativa se fija aparte para que
  // el navegador no acepte el envío con una contraseña que no cumple RF-7. Ese mensaje llega en el
  // `data-mensaje` que pinta la vista desde el locale, para que no haya dos copias del mismo texto.
  marcaEstadoDelCampoPwd (haEmpezadoAEscribir, cumpleTodos) {
    const error = this.inputPwdTarget.nextElementSibling
    this.inputPwdTarget.classList.toggle(CLASE_CAMPO_VALIDO, cumpleTodos)

    if (!haEmpezadoAEscribir) {
      // Enfocar el campo vacío no marca ni error ni acierto: el caption neutro es todo el feedback.
      this.notifySet(this.inputPwdTarget, error, '')
      this.inputPwdTarget.setCustomValidity('')
      this.resuelveCampo(CAMPO_PASSWORD)
    } else if (cumpleTodos) {
      this.notifySuccess(this.inputPwdTarget, error, '')
      this.resuelveCampo(CAMPO_PASSWORD)
    } else {
      this.notifySet(this.inputPwdTarget, error, '#e91101')
      this.inputPwdTarget.setCustomValidity(this.requisitosPwdTarget.dataset.mensaje)
      // No cumplir mientras se teclea es el estado normal de la checklist: el error se cobra al
      // salir del campo con la contraseña todavía sin cumplir. TC-134-1.4.5.3.
      this.anotaErrorPendiente(CAMPO_PASSWORD, ERROR_PASSWORD_DEBIL)
    }

    // Dos de las tres ramas fijan la validez sin pasar por `notify`, así que el recálculo del botón
    // se hace aquí para las tres.
    this.actualizaBotonDeEnvio()
  }

  // Indicador de fortaleza orientativo (TC-134-1.1.6.2). No toca la validez del campo ni el estado
  // de la checklist: una contraseña que cumple los tres requisitos de RF-7 se envía igual aunque
  // el indicador la marque "Débil". Solo pinta.
  pintaFortalezaPwd (valor) {
    if (!this.hasFortalezaPwdTarget) return

    this.cargaMotorDeFortaleza()

    // El indicador aparece con el primer carácter escrito, pero solo cuando ya hay motor con el
    // que calcularlo: enseñar una barra mientras el diccionario viaja sería anunciar una fortaleza
    // que todavía no se ha medido.
    this.fortalezaPwdTarget.hidden = valor.length === 0 || !this.motorDeFortaleza
    if (this.fortalezaPwdTarget.hidden) return

    const { nivel, etiqueta } = FORTALEZA_POR_SCORE[this.motorDeFortaleza(valor)]

    // Solo se escribe en el DOM cuando el nivel cambia de verdad. Además de ahorrarse el trabajo,
    // evita que el `aria-live` de la etiqueta repita el mismo anuncio en cada pulsación.
    if (this.fortalezaPwdTarget.dataset.nivel === nivel) return

    this.fortalezaPwdTarget.dataset.nivel = nivel
    this.etiquetaFortalezaPwdTarget.textContent = etiqueta
  }

  // Trae el pack `password_strength` la primera vez que el usuario toca el campo Contraseña, desde
  // la URL que la vista ha resuelto por el pipeline de assets. Es idempotente: las llamadas
  // siguientes ven la carga ya pedida y no hacen nada.
  //
  // Si el script no llega (red caída, bloqueador, pack sin compilar), el indicador se queda oculto
  // y el formulario funciona exactamente igual — es orientativo, no una validación.
  cargaMotorDeFortaleza () {
    if (this.motorDeFortaleza || this.motorDeFortalezaPedido) return

    // En una navegación de Turbo el pack puede seguir ejecutado de una visita anterior: se
    // reaprovecha en vez de volver a inyectarlo.
    if (window.RankiaFortalezaDeContrasena) {
      this.motorDeFortaleza = window.RankiaFortalezaDeContrasena
      return
    }

    this.motorDeFortalezaPedido = true

    const script = document.createElement('script')
    script.src = this.fortalezaPwdTarget.dataset.motor
    script.async = true
    script.addEventListener('load', () => {
      this.motorDeFortaleza = window.RankiaFortalezaDeContrasena
      // El usuario puede haber escrito mientras el script viajaba: se repinta con lo que ya haya.
      this.pintaFortalezaPwd(this.inputPwdTarget.value)
    })
    document.head.appendChild(script)
  }
}
